/**
 * Matching rules shared by the evidence upload modal.
 *
 * Evidence is matched to samples and files by *normalized* keys downstream (backend
 * normalizeSampleRef / sampleLabelKey, AI service _normalize_ref and _filename_matches).
 * The modal applies the same rules so the UI never lets through what the pipeline would
 * silently merge.
 */

/* ── Sample labels ─────────────────────────────────────────────────────────── */

/**
 * Key a sample label is matched on: surrounding quotes removed, whitespace collapsed,
 * lower-cased. Mirrors backend sampleLabelKey and AI-service _normalize_ref.
 */
export function sampleLabelKey(label) {
  return String(label ?? '')
    .replace(/^"+|"+$/g, '')
    .replace(/^'+|'+$/g, '')
    .trim()
    .split(/\s+/)
    .join(' ')
    .toLowerCase();
}

/** The existing label that `candidate` would collide with, or null. */
export function findLabelCollision(existingLabels, candidate) {
  const key = sampleLabelKey(candidate);
  if (!key) return null;
  return (existingLabels || []).find((l) => sampleLabelKey(l) === key) ?? null;
}

/** `base`, or `base (2)`, `base (3)`… — the first variant that collides with nothing. */
export function uniqueSampleLabel(base, existingLabels) {
  let label = base;
  for (let n = 2; findLabelCollision(existingLabels, label); n += 1) {
    label = `${base} (${n})`;
  }
  return label;
}

/* ── Evidence files ────────────────────────────────────────────────────────── */

const category = (ev) => ev.file_category || 'evidence';

/**
 * Key the AI service matches a file on (_filename_matches): extension dropped,
 * lower-cased, spaces as underscores. Two files with the same key cannot be told apart
 * downstream — one would overwrite the other when downloaded.
 */
export function fileNameKey(filename) {
  return String(filename ?? '')
    .replace(/\.[^./\\]+$/, '')
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Collapse true re-uploads only: same category, same name and same size. Files that share
 * a name but differ in size are different documents and are all kept. The newest row wins.
 */
export function dedupeEvidenceFiles(rows) {
  const seen = new Map();
  for (const e of rows) {
    const key = `${category(e)}::${e.filename}::${e.file_size ?? ''}`;
    if (!seen.has(key) || e.id > seen.get(key).id) seen.set(key, e);
  }
  return Array.from(seen.values());
}

function formatSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return null;
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Tag files that share a name with another file in the list with a version label
 * (v1 = oldest) and a tooltip with size and upload date, so they can be told apart.
 * Files with a unique name are returned untouched.
 */
export function labelSameNameVersions(files) {
  const groups = new Map();
  for (const f of files) {
    const key = `${category(f)}::${String(f.filename ?? '').toLowerCase()}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(f);
  }
  const byId = new Map();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    [...group].sort((a, b) => a.id - b.id).forEach((f, i) => {
      const detail = [formatSize(f.file_size), formatDate(f.uploaded_at)].filter(Boolean).join(' · ');
      byId.set(f.id, { _versionLabel: `v${i + 1}`, _versionTitle: detail ? `Version ${i + 1} · ${detail}` : `Version ${i + 1}` });
    });
  }
  return files.map((f) => (byId.has(f.id) ? { ...f, ...byId.get(f.id) } : f));
}

/**
 * An evidence file already in the test that `candidate` would be confused with
 * downstream (same name key), or null. Only evidence-category files are tested.
 */
export function findSameNameConflict(selectedFiles, candidate) {
  if (category(candidate) !== 'evidence') return null;
  const key = fileNameKey(candidate.filename);
  if (!key) return null;
  return (
    selectedFiles.find(
      (f) => f.id !== candidate.id && category(f) === 'evidence' && fileNameKey(f.filename) === key,
    ) ?? null
  );
}

/** Groups of selected evidence files whose names cannot be told apart downstream. */
export function sameNameGroups(selectedFiles) {
  const groups = new Map();
  for (const f of selectedFiles) {
    if (category(f) !== 'evidence') continue;
    const key = fileNameKey(f.filename);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(f);
  }
  return [...groups.values()].filter((g) => g.length > 1);
}
